import React, { useState, useEffect, useCallback } from 'react';
import { googleCalendarAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { CalendarCheck, CalendarX, Loader2, ExternalLink, RefreshCw, AlertTriangle, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import { formatDate, getErrorMessage } from '@/lib/utils';

const STATUS_TONE = {
  synced: { icon: CheckCircle2, label: 'Synced', className: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  user_not_connected: { icon: AlertTriangle, label: 'Not connected yet', className: 'text-amber-700 bg-amber-50 border-amber-200' },
  no_linked_user: { icon: AlertTriangle, label: 'No linked user', className: 'text-amber-700 bg-amber-50 border-amber-200' },
  api_error: { icon: AlertTriangle, label: 'API error', className: 'text-rose-700 bg-rose-50 border-rose-200' },
  error: { icon: AlertTriangle, label: 'Error', className: 'text-rose-700 bg-rose-50 border-rose-200' },
  pending: { icon: RefreshCw, label: 'Pending', className: 'text-slate-600 bg-slate-50 border-slate-200' },
};

function SyncStatusBadge({ status }) {
  const meta = STATUS_TONE[status] || STATUS_TONE.pending;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium border rounded px-1.5 py-0.5 ${meta.className}`}>
      <Icon className="h-3 w-3" /> {meta.label}
    </span>
  );
}

// Format an ISO timestamp for the "Last sync" summary — keeps the
// date + minute precision without seconds so the diagnostic panel
// stays scannable at a glance.
function formatSyncedAt(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return null; }
}

// Highest sync timestamp across events and follow-ups — surfaced as
// "Last sync" so the user gets a single, obvious signal that sync is
// alive.
function deriveLastSync(diag) {
  if (!diag) return null;
  const times = [];
  for (const p of diag.participant_assignments || []) {
    if (p.participant_google_synced_at) times.push(p.participant_google_synced_at);
  }
  for (const f of diag.lead_follow_ups || []) {
    if (f.google_synced_at) times.push(f.google_synced_at);
  }
  if (!times.length) return null;
  times.sort();
  return times[times.length - 1];
}

/**
 * Google Calendar integration card — one-way push sync.
 *
 * When the URL includes ?google=connected or ?google=error the OAuth
 * callback has just landed the user back on this page, so we show a
 * toast and clean the query params. Status is refreshed on mount.
 *
 * Phase 27.30.14 — Adds a diagnostics section showing the last 10
 * event registrations and lead follow-ups with their per-item sync
 * status + a "Sync now" button that manually re-pushes an item that
 * failed to sync automatically (usually because the user had not
 * connected Google yet when the item was originally created).
 */
export function GoogleCalendarIntegration() {
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [resyncing, setResyncing] = useState(null);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await googleCalendarAPI.status();
      setStatus(res.data);
    } catch (err) {
      console.error('Failed to load Google Calendar status', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDiagnostics = useCallback(async () => {
    setDiagLoading(true);
    try {
      const res = await googleCalendarAPI.diagnostics();
      setDiag(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load sync diagnostics'));
    } finally {
      setDiagLoading(false);
    }
  }, []);

  useEffect(() => {
    // Handle OAuth callback landing query params.
    const params = new URLSearchParams(window.location.search);
    const flag = params.get('google');
    if (flag === 'connected') {
      sessionStorage.removeItem('gcal_prompt_connecting');
      sessionStorage.removeItem('gcal_prompt_dismissed_session');
      localStorage.removeItem('gcal_prompt_dismissed_at');
      toast.success('Google Calendar connected successfully');
      window.history.replaceState({}, '', window.location.pathname);
      // Refresh the auth user so the freshly-imported Google avatar
      // (photo_url set by maybe_import_google_profile_photo on the
      // OAuth callback) appears in the sidebar without a hard reload.
      refreshUser?.().catch(() => { /* silent */ });
    } else if (flag === 'error') {
      sessionStorage.removeItem('gcal_prompt_connecting');
      const reason = params.get('reason') || 'unknown';
      const detail = params.get('detail');
      // Surface Google's actual error text when available — critical
      // for diagnosing prod-only misconfigs (invalid_client, redirect
      // mismatch, etc.) that a generic "token_exchange_failed" hides.
      toast.error(
        detail
          ? `Could not connect Google Calendar (${reason}): ${detail}`
          : `Could not connect Google Calendar (${reason})`,
        { duration: 15000 },
      );
      window.history.replaceState({}, '', window.location.pathname);
    }
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (diagOpen && !diag) loadDiagnostics();
  }, [diagOpen, diag, loadDiagnostics]);

  // Auto-load diagnostics once the user is confirmed connected so the
  // "Last sync" summary and error count appear on the panel header
  // without needing to expand the details.
  useEffect(() => {
    if (status?.connected && !diag && !diagLoading) {
      loadDiagnostics();
    }
  }, [status?.connected, diag, diagLoading, loadDiagnostics]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await googleCalendarAPI.getAuthUrl('/profile');
      // Redirect the current window to Google's consent page.
      window.location.href = res.data.authorization_url;
    } catch (err) {
      toast.error('No se pudo iniciar la conexión con Google');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect your Google Calendar account? Events already synced will remain on your calendar.')) {
      return;
    }
    setDisconnecting(true);
    try {
      await googleCalendarAPI.disconnect();
      toast.success('Google Calendar disconnected');
      setDiag(null);
      await loadStatus();
    } catch (err) {
      toast.error('Could not disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  const resyncEvent = async (eventId) => {
    setResyncing(`ev:${eventId}`);
    try {
      const r = await googleCalendarAPI.resyncEvent(eventId);
      const ok = r.data?.participant_google_sync_status === 'synced';
      if (ok) toast.success('Event synced to Google Calendar');
      else toast.error(`Sync status: ${r.data?.participant_google_sync_status || 'unknown'}`);
      await loadDiagnostics();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Resync failed'));
    } finally {
      setResyncing(null);
    }
  };

  const resyncFollowUp = async (followUpId) => {
    setResyncing(`fu:${followUpId}`);
    try {
      const r = await googleCalendarAPI.resyncFollowUp(followUpId);
      const ok = r.data?.google_sync_status === 'synced';
      if (ok) toast.success('Follow-up synced to Google Calendar');
      else toast.error(`Sync status: ${r.data?.google_sync_status || 'unknown'}`);
      await loadDiagnostics();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Resync failed'));
    } finally {
      setResyncing(null);
    }
  };

  // Manual re-import of the Google profile photo. Needed when the
  // OAuth-time auto-import didn't apply — typically because the agent
  // record didn't exist yet or the user_id ↔ npn link was stale.
  const [syncingPhoto, setSyncingPhoto] = useState(false);
  const resyncPhoto = async () => {
    setSyncingPhoto(true);
    try {
      const r = await googleCalendarAPI.resyncPhoto();
      if (r.data?.photo_synced) {
        toast.success('Profile photo imported from Google — refresh the page to see it');
      } else {
        toast.info('Google did not return a profile photo for this account');
      }
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not sync photo from Google'));
    } finally {
      setSyncingPhoto(false);
    }
  };

  return (
    <Card data-testid="google-calendar-integration-card">
      <CardHeader>
        <CardTitle className="text-lg font-heading flex items-center gap-2">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="4" width="18" height="17" rx="2" fill="#4285F4"/>
            <path d="M3 8h18" stroke="white" strokeWidth="0.5"/>
            <text x="12" y="17" textAnchor="middle" fill="white" fontSize="8" fontWeight="700">31</text>
          </svg>
          Google Calendar
          {status?.connected && (
            <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 border-emerald-200">
              Connected
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 py-2">
            <Loader2 size={16} className="animate-spin" />
            Loading status...
          </div>
        ) : status?.connected ? (
          <>
            <div className="text-sm space-y-1">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-slate-600">
                  <CalendarCheck size={16} className="text-emerald-500" />
                  <span>Linked account:</span>
                  <span className="font-medium text-slate-900">{status.google_email}</span>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={resyncPhoto}
                  disabled={syncingPhoto}
                  className="h-7 text-[11px]"
                  data-testid="gcal-resync-photo"
                >
                  {syncingPhoto ? (
                    <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Syncing…</>
                  ) : (
                    <><RefreshCw className="h-3 w-3 mr-1" /> Sync photo from Google</>
                  )}
                </Button>
              </div>
              {status.connected_at && (
                <div className="text-xs text-slate-500 pl-6">
                  Connected since {formatDate(status.connected_at)}
                </div>
              )}
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm text-slate-600">
              <p className="font-medium text-slate-700 mb-1">One-way sync active</p>
              <ul className="list-disc pl-5 space-y-0.5 text-xs">
                <li>Lead Follow-ups are added to your calendar with a reminder 1h before</li>
                <li>Events you create are added to your calendar</li>
                <li>CRM tasks assigned to you are added to your calendar</li>
                <li>Changes in KR Agency Hub are reflected automatically</li>
                <li>Changes in Google Calendar <span className="font-medium">do not</span> flow back to KR Agency Hub</li>
              </ul>
            </div>

            {/* Diagnostics + manual resync */}
            <div className="border border-slate-200 rounded-md" data-testid="gcal-diagnostics-section">
              <button
                type="button"
                className="w-full flex items-center justify-between px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                onClick={() => setDiagOpen((v) => !v)}
                data-testid="gcal-diagnostics-toggle"
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="font-medium">Sync diagnostics</span>
                  {(() => {
                    if (!diag) return null;
                    const last = deriveLastSync(diag);
                    const errCount = [
                      ...(diag.participant_assignments || []).filter(
                        (p) => p.participant_google_sync_status
                          && !['synced', 'pending'].includes(p.participant_google_sync_status),
                      ),
                      ...(diag.lead_follow_ups || []).filter(
                        (f) => f.google_sync_status
                          && !['synced', 'pending'].includes(f.google_sync_status),
                      ),
                    ].length;
                    return (
                      <span className="flex items-center gap-2 text-xs text-slate-500 truncate">
                        <span data-testid="gcal-diagnostics-summary">
                          Last sync: <span className="text-slate-700">{last ? formatSyncedAt(last) : 'Never'}</span>
                        </span>
                        {errCount > 0 && (
                          <span
                            data-testid="gcal-diagnostics-error-count"
                            className="inline-flex items-center gap-0.5 text-rose-700 bg-rose-50 border border-rose-200 rounded px-1.5 py-0.5 text-[10px] font-medium"
                          >
                            <AlertTriangle className="h-3 w-3" /> {errCount} error{errCount > 1 ? 's' : ''}
                          </span>
                        )}
                      </span>
                    );
                  })()}
                </div>
                {diagOpen ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />}
              </button>
              {diagOpen && (
                <div className="p-3 border-t border-slate-200 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-slate-500">
                        Recent registrations and follow-ups. Items that failed
                        to sync automatically can be re-pushed manually.
                      </p>
                      {/* Overall "Last sync" — biggest signal that sync is alive. */}
                      <p className="text-[11px] text-slate-600 mt-1" data-testid="gcal-last-sync">
                        <span className="font-medium">Last sync:</span>{' '}
                        {(() => {
                          const last = deriveLastSync(diag);
                          return last ? formatSyncedAt(last) : 'Never';
                        })()}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={loadDiagnostics}
                      disabled={diagLoading}
                      className="h-7 text-[11px]"
                      data-testid="gcal-diagnostics-refresh"
                    >
                      <RefreshCw className={`h-3 w-3 mr-1 ${diagLoading ? 'animate-spin' : ''}`} /> Refresh
                    </Button>
                  </div>

                  {/* Participant events */}
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                      Event registrations
                    </div>
                    {diag?.participant_assignments?.length ? (
                      <ul className="space-y-1.5" data-testid="gcal-diagnostics-events">
                        {diag.participant_assignments.map((p) => (
                          <li
                            key={p.assignment_id}
                            className="flex flex-col gap-1 text-xs border-b border-slate-100 pb-1.5 last:border-b-0 last:pb-0"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <SyncStatusBadge status={p.participant_google_sync_status || 'pending'} />
                                <span className="truncate font-mono text-slate-500">{p.event_id.slice(0, 8)}</span>
                                {p.registration_status && (
                                  <Badge variant="outline" className="text-[10px]">{p.registration_status}</Badge>
                                )}
                                {p.participant_google_synced_at && (
                                  <span
                                    className="text-slate-400 text-[10px] truncate"
                                    title={p.participant_google_synced_at}
                                  >
                                    {formatSyncedAt(p.participant_google_synced_at)}
                                  </span>
                                )}
                              </div>
                              {p.registration_status === 'registered'
                                && p.participant_google_sync_status !== 'synced' && (
                                <Button
                                  size="sm" variant="ghost"
                                  className="h-6 text-[11px]"
                                  disabled={resyncing === `ev:${p.event_id}`}
                                  onClick={() => resyncEvent(p.event_id)}
                                  data-testid={`gcal-resync-event-${p.event_id}`}
                                >
                                  {resyncing === `ev:${p.event_id}` ? 'Syncing…' : 'Sync now'}
                                </Button>
                              )}
                            </div>
                            {p.participant_google_sync_error && (
                              <p
                                className="text-[10px] text-rose-600 pl-1 truncate"
                                title={p.participant_google_sync_error}
                                data-testid={`gcal-event-error-${p.event_id}`}
                              >
                                {p.participant_google_sync_error}
                              </p>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-slate-400">No recent registrations.</p>
                    )}
                  </div>

                  {/* Lead follow-ups */}
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                      Lead follow-ups
                    </div>
                    {diag?.lead_follow_ups?.length ? (
                      <ul className="space-y-1.5" data-testid="gcal-diagnostics-followups">
                        {diag.lead_follow_ups.map((f) => (
                          <li
                            key={f.follow_up_id}
                            className="flex flex-col gap-1 text-xs border-b border-slate-100 pb-1.5 last:border-b-0 last:pb-0"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <SyncStatusBadge status={f.google_sync_status || 'pending'} />
                                <span className="truncate font-mono text-slate-500">{f.follow_up_id.slice(0, 8)}</span>
                                <span className="text-slate-500 truncate">{f.scheduled_at?.slice(0, 16).replace('T', ' ')}</span>
                                {f.google_synced_at && (
                                  <span
                                    className="text-slate-400 text-[10px] truncate"
                                    title={f.google_synced_at}
                                  >
                                    {formatSyncedAt(f.google_synced_at)}
                                  </span>
                                )}
                              </div>
                              {!f.done && f.google_sync_status !== 'synced' && (
                                <Button
                                  size="sm" variant="ghost"
                                  className="h-6 text-[11px]"
                                  disabled={resyncing === `fu:${f.follow_up_id}`}
                                  onClick={() => resyncFollowUp(f.follow_up_id)}
                                  data-testid={`gcal-resync-followup-${f.follow_up_id}`}
                                >
                                  {resyncing === `fu:${f.follow_up_id}` ? 'Syncing…' : 'Sync now'}
                                </Button>
                              )}
                            </div>
                            {f.google_sync_error && (
                              <p
                                className="text-[10px] text-rose-600 pl-1 truncate"
                                title={f.google_sync_error}
                                data-testid={`gcal-followup-error-${f.follow_up_id}`}
                              >
                                {f.google_sync_error}
                              </p>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-slate-400">No recent follow-ups.</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <Button
              variant="outline"
              onClick={handleDisconnect}
              disabled={disconnecting}
              data-testid="google-calendar-disconnect-btn"
              className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
            >
              {disconnecting ? <Loader2 size={14} className="mr-2 animate-spin" /> : <CalendarX size={14} className="mr-2" />}
              Disconnect Google Calendar
            </Button>
          </>
        ) : (
          <>
            <div className="text-sm text-slate-600">
              Connect your Google account to automatically sync your follow-ups, events, and CRM tasks
              to your personal calendar, with a reminder 1 hour before.
            </div>
            <Button
              onClick={handleConnect}
              disabled={connecting}
              data-testid="google-calendar-connect-btn"
              className="bg-[#4285F4] hover:bg-[#357ae8] text-white"
            >
              {connecting ? (
                <><Loader2 size={14} className="mr-2 animate-spin" /> Redirecting...</>
              ) : (
                <><ExternalLink size={14} className="mr-2" /> Connect Google Calendar</>
              )}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
